import { useContext, useEffect, useRef, useState } from "react";
import { FeedEntry, Medium } from "protogen/feed_pb";
import { AccountStub, Attachment } from "protogen/common_pb";
import { FeedFocusState } from "../../types/feed";
import { CurrentUserContext } from "../context/RequireAuth";
import { isToday } from "../../common/utils";
import DateDisplay from "../common/DateDisplay";
import CallPreview from "./CallPreview";
import VideoCallPreview from "./VideoCallPreview";
import TaskComment from "./TaskComment";
import TaskChangeEvent from "./TaskChangeEvent";
import EmailPreview from "./EmailPreview";
import { actionForEntry } from "./FeedEntryActionIcons";
import ActivityFeedEntry from "./ActivityFeedEntry";
import { Avatar, Box, Typography } from "@mui/material";
import ActivityDivider from "./ActivityDivider";
import TextMessage, {
  errorExtraInfo,
  errorMessageForCode,
  errorRetryable,
} from "./TextMessage";
import { TextMessageStatus } from "protogen/conversation_pb";
import LockOpenIcon from "@heroicons/react/24/outline/LockOpenIcon";
import HighlightProvider from "../context/HighlightProvider";
import ScheduledMessageByline from "./ScheduledMessageByline";
import useScheduleMessage from "./useScheduleMessage";
import DocumentActivity from "./DocumentActivity";
import SecretActivity from "./SecretActivity";
import DelegationTaskComment from "./DelegationTaskComment";
import DelegationTaskCreated from "./DelegationTaskCreated";
import { Bot } from "lucide-react";

const areArraysEqual = (a: string[], b: string[]) => {
  const s1 = new Set(a);
  const s2 = new Set(b);
  if (s1 === s2) return true;
  if (s1.size !== s2.size) return false;
  for (const item of Array.from(s1.values())) {
    if (!s2.has(item)) return false;
  }
  return true;
};

const LoginLinkEntry = ({
  entry,
  user,
}: {
  entry: FeedEntry;
  user: AccountStub;
}) => {
  return (
    <Box
      key={entry.ref}
      sx={{
        width: "100%",
        backgroundColor: "#FFF",
        padding: "0px 16px",
        border: "1px solid #F2F2F2",
        color: "#3D3D3D",
        transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        borderRadius: "4px",
        boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.11)",
        overflow: "hidden",
        maxWidth: "345px",
        height: "48px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <LockOpenIcon height={17} width={17} />
      <Typography color="text.secondary" variant="bodySmall">
        {user.firstName} logged into Faye
      </Typography>
    </Box>
  );
};

const COLLAPSED_HEIGHT = 48;

const BotMessageWrapper = ({
  children,
  alignLeft,
  errorText,
  defaultExpanded,
}: {
  children: React.ReactNode;
  alignLeft: boolean;
  errorText?: string;
  defaultExpanded?: boolean;
}) => {
  const [collapsed, setCollapsed] = useState(!defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fullHeight, setFullHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setFullHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  const overflows = fullHeight > COLLAPSED_HEIGHT;
  const isClipped = collapsed && overflows;
  return (
    <Box>
      <Box
        onClick={() => isClipped && setCollapsed(false)}
        sx={{
          position: "relative",
          height: isClipped ? `${COLLAPSED_HEIGHT}px` : `${fullHeight}px`,
          overflow: "hidden",
          cursor: isClipped ? "pointer" : "default",
          transition: "height 0.25s ease-out",
          borderRadius: alignLeft ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
          ...(errorText ? { border: "1px solid #FECACA" } : {}),
        }}
      >
        <div ref={contentRef}>{children}</div>
        {isClipped && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "24px",
              background:
                "linear-gradient(transparent, rgba(237, 233, 254, 0.95))",
              borderRadius: "0 0 16px 16px",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
      {errorText && (
        <Typography
          sx={{
            fontSize: "11px",
            color: "#9CA3AF",
            mt: "2px",
            ml: "4px",
          }}
        >
          {errorText}
        </Typography>
      )}
    </Box>
  );
};

type BotColorScheme = {
  botResponse: string;
  botError: string;
  userMessage: string;
  iconColor: string;
  linkColor?: string;
};

type FeedEntryProps = {
  entry: FeedEntry;
  // Used to format things in a sequence.
  nextEntry?: FeedEntry;
  prevEntry?: FeedEntry;
  isCenteredEntry?: boolean;
  accountMap: Record<string, AccountStub>;
  feedFocusState: FeedFocusState;
  setFeedFocusState: (s: FeedFocusState) => void;
  openAttachment: (a: Attachment) => void;
  onBookmark?: () => void;
  noAvatars?: boolean;
  familyRef?: string;
  handleRetry?: () => void;
  handleRefresh?: () => void;
  handleRemove?: () => void;
  botExpanded?: boolean;
  botColorScheme?: BotColorScheme;
  formatMarkdown?: boolean;
};
export default ({
  entry,
  accountMap,
  feedFocusState,
  setFeedFocusState,
  nextEntry,
  prevEntry,
  isCenteredEntry,
  openAttachment,
  onBookmark,
  noAvatars,
  familyRef,
  handleRetry,
  handleRefresh,
  handleRemove,
  botExpanded,
  botColorScheme,
  formatMarkdown,
}: FeedEntryProps) => {
  const currentUser = useContext(CurrentUserContext);
  const scheduledManager = useScheduleMessage({
    messageRef: entry.ref,
    onSent: handleRefresh || (() => {}),
    onDeleted: handleRemove || (() => {}),
  });
  const entryDt = new Date(Number(entry.timestampSec) * 1000);
  const time = DateDisplay({ date: entryDt });
  const sender = accountMap[entry.senderRef];
  const loginLinkText =
    entry?.textMessage &&
    /\s*Welcome! Your login url is: .+\/member-login-setup\/[a-zA-Z0-9]+\s*/.test(
      entry.textMessage.content,
    );
  // Helpful if you get confused.
  // console.log(entry.textMessage?.content, ' between ', prevEntry?.textMessage?.content, ' and ', nextEntry?.textMessage?.content);
  const SAME_TS_THRESHOLD_SEC = 90 * 60;
  const includeSender =
    !loginLinkText &&
    (!prevEntry ||
      prevEntry.senderRef !== entry.senderRef ||
      !areArraysEqual(prevEntry.recipientRefs, entry.recipientRefs));

  const prevEntryDate = prevEntry
    ? new Date(Number(prevEntry.timestampSec) * 1000)
    : null;
  const entryDate = new Date(Number(entry.timestampSec) * 1000);

  const includeTime =
    !nextEntry ||
    Number(nextEntry.timestampSec) - Number(entry.timestampSec) >
      SAME_TS_THRESHOLD_SEC ||
    (prevEntryDate &&
      prevEntryDate.toDateString() !== entryDate.toDateString());
  const includeHeader = includeSender || includeTime;
  const includeAvatar =
    !noAvatars && includeSender && sender && !sender.isAdvisor;
  const includeTodayDivider =
    isToday(entryDt) &&
    (!prevEntry || !isToday(new Date(Number(prevEntry.timestampSec) * 1000)));

  const getSubcomponent = (medium: Medium) => {
    if (loginLinkText) {
      return (
        <LoginLinkEntry
          entry={entry}
          user={accountMap[entry.recipientRefs[0]]}
        />
      );
    }
    switch (medium) {
      case Medium.SMS:
      case Medium.SMS_BOT:
      case Medium.LINQ_IMESSAGE:
        const isVoiceMemo =
          entry.textMessage?.attachments.length === 1 &&
          entry.textMessage?.attachments[0].contentType.startsWith("audio/mp"); // Handle mp3 and mp4
        const voiceMemoTranscript =
          entry.textMessage?.documentSuggestion?.details;
        const unconfirmedDelivery =
          // consider it unconfirmed if it's pending and sent more than 5 minutes ago
          entry.textMessage!.status === TextMessageStatus.STATUS_PENDING &&
          entry.textMessage?.timestampSec < Date.now() / 1000 - 300;
        const isBotConversation = entry.textMessage?.isBotConversation;
        const isBotResponse = isBotConversation && !entry.senderRef;
        const isBotError =
          isBotResponse &&
          entry.textMessage!.status === TextMessageStatus.STATUS_UNDELIVERED;
        const botBubbleColor = isBotError
          ? botColorScheme?.botError || "#FEF2F2"
          : isBotResponse
            ? botColorScheme?.botResponse || "#F5F3FF"
            : isBotConversation
              ? botColorScheme?.userMessage || "#EDE9FE"
              : undefined;
        const botTextColor = isBotConversation ? "#3D3D3D" : undefined;
        const botBubbleSx = isBotConversation
          ? {
              padding: "6px 10px",
              fontSize: "13px",
              borderRadius: 0,
              "& .MuiTypography-root": {
                fontSize: "13px",
                fontWeight: 400,
                color: "#3D3D3D",
              },
              "& a, & .ProseMirror a": {
                color: `${botColorScheme?.linkColor || "blue"} !important`,
                fontWeight: 500,
              },
            }
          : {};
        const textMessageNode = (
          <HighlightProvider
            familyRef={familyRef}
            entityRef={entry.textMessage!.ref}
            entityType={"message"}
          >
            <TextMessage
              byline={
                entry.textMessage!.status ===
                TextMessageStatus.STATUS_SCHEDULED ? (
                  <ScheduledMessageByline
                    scheduledManager={scheduledManager}
                    scheduledTime={entry.textMessage!.scheduledSecUTC}
                  />
                ) : undefined
              }
              loading={
                !isBotResponse &&
                entry.textMessage!.status === TextMessageStatus.STATUS_PENDING
              }
              error={
                !isBotResponse &&
                entry.textMessage!.status ===
                  TextMessageStatus.STATUS_UNDELIVERED
                  ? errorMessageForCode(entry.textMessage!.errorCode)
                  : undefined
              }
              errorExtraInfo={
                !isBotResponse &&
                entry.textMessage!.status ===
                  TextMessageStatus.STATUS_UNDELIVERED
                  ? errorExtraInfo(entry.textMessage?.errorCode)
                  : undefined
              }
              warning={
                !isBotResponse && unconfirmedDelivery
                  ? "Message delivery not confirmed"
                  : undefined
              }
              warningTooltip={
                !isBotResponse && unconfirmedDelivery
                  ? "Message has been sent but the carrier has not confirmed delivery. This could be because the phone is turned off or out of service."
                  : undefined
              }
              handleRetry={
                !isBotResponse &&
                entry.textMessage!.status ===
                  TextMessageStatus.STATUS_UNDELIVERED &&
                errorRetryable(entry.textMessage?.errorCode)
                  ? handleRetry
                  : undefined
              }
              alignLeft={isBotResponse ? false : !(sender && sender.isAdvisor)}
              content={entry.textMessage!.content}
              attachments={entry.textMessage!.attachments}
              openAttachment={openAttachment}
              sx={{
                ...(botBubbleColor
                  ? {
                      backgroundColor: botBubbleColor,
                      background: botBubbleColor,
                      color: botTextColor,
                    }
                  : !(sender && sender.isAdvisor)
                    ? { backgroundColor: "#FFF" }
                    : { backgroundColor: "#1C9191" }),
                ...botBubbleSx,
              }}
              bubbleSx={botBubbleSx}
              isVoiceMemo={isVoiceMemo}
              voiceMemoTranscript={voiceMemoTranscript}
              formatMarkdown={formatMarkdown}
            />
          </HighlightProvider>
        );
        if (isBotConversation) {
          return (
            <BotMessageWrapper
              alignLeft={isBotResponse ? false : !(sender && sender.isAdvisor)}
              errorText={isBotError ? "Message failed to send" : undefined}
              defaultExpanded={botExpanded}
            >
              {textMessageNode}
            </BotMessageWrapper>
          );
        }
        return textMessageNode;
      case Medium.PHONE:
        return (
          <CallPreview
            call={entry.phoneCall!}
            participants={entry.recipientRefs.map((r) => accountMap[r])}
          />
        );
      case Medium.VIDEO:
        return (
          <VideoCallPreview
            videoRoom={entry.videoRoom!}
            participants={entry.recipientRefs.map((r) => accountMap[r])}
          />
        );
      case Medium.EMAIL:
        return (
          <EmailPreview
            isAdvisor={sender && sender.isAdvisor}
            email={entry.emailMessage!}
            feedFocusState={feedFocusState}
            setFeedFocusState={setFeedFocusState}
          />
        );
      case Medium.TASK_COMMENT:
        return <TaskComment block={entry.taskBlock!} task={entry.task!} />;
      case Medium.TASK_CHANGE_EVENT:
        return (
          <TaskChangeEvent
            task={entry.task!}
            taskChangeEvent={entry.taskChangeEvent!}
            accountMap={accountMap}
            senderRef={entry.senderRef}
          />
        );
      case Medium.DELEGATION_TASK_COMMENT:
        return (
          <DelegationTaskComment
            delegationTask={entry.delegationTask!}
            comment={entry.delegationTaskComment!}
          />
        );
      case Medium.DELEGATION_TASK_CREATED:
        return (
          <DelegationTaskCreated
            delegationTask={entry.delegationTask!}
            creatorName={
              currentUser.ref === entry.senderRef
                ? "You"
                : `${sender.firstName}`
            }
          />
        );
      case Medium.DOCUMENT:
        return (
          <DocumentActivity
            unread={entry.unread}
            document={entry.document!}
            creatorName={
              currentUser.ref === entry.senderRef
                ? "You"
                : `${sender.firstName}`
            }
          />
        );
      case Medium.SECRET:
        return (
          <SecretActivity
            secretName={entry.secretName}
            secretRef={entry.ref}
            familyRef={familyRef || ""}
            creatorName={
              currentUser.ref === entry.senderRef
                ? "You"
                : `${sender.firstName}`
            }
          />
        );
      default:
        return null;
    }
  };
  let action = actionForEntry(entry, familyRef);
  const isBotMsg = entry.textMessage?.isBotConversation;
  const isBotResp = isBotMsg && !entry.senderRef;
  const buildHeader = (): string => {
    if (loginLinkText) return "";
    if (isBotResp) return "Faye AI";
    if (isBotMsg && sender) return `${sender.firstName} → Faye AI`;
    if (!sender && entry.medium === Medium.EMAIL) {
      return entry.emailMessage?.fromAddress || "";
    }

    if (
      entry.medium === Medium.TASK_COMMENT ||
      entry.medium === Medium.TASK_CHANGE_EVENT ||
      entry.medium === Medium.DELEGATION_TASK_COMMENT ||
      entry.medium === Medium.DELEGATION_TASK_CREATED
    ) {
      // We will include the sender name in the body
      return "";
    }

    if (sender && !sender.isAdvisor) {
      if (entry.recipientRefs.length > 1) {
        const otherRecipients = entry.recipientRefs
          .filter((r) => !(accountMap[r]?.isAdvisor || r === sender.ref))
          .map((r) => accountMap[r]?.firstName || "Unknown")
          .join(", ");
        return `${sender.firstName} → ${otherRecipients}`;
      }
      return `${sender.firstName}`;
    }
    const senderName =
      currentUser.ref === entry.senderRef
        ? "You"
        : `${sender?.firstName ?? "Faye AI"}`;
    if (!entry.recipientRefs.length || !accountMap[entry.recipientRefs[0]]) {
      return senderName;
    }
    return `${senderName} → ${entry.recipientRefs
      .map((r) => accountMap[r]?.firstName || "Unknown")
      .join(", ")}`;
  };

  useEffect(() => {
    const highlightedElement = document.querySelector(
      "[data-is-highlighted='true']",
    );
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [isCenteredEntry]);
  const entryToBookmarks = (e: FeedEntry) => {
    if (e.medium === Medium.EMAIL) {
      return e.emailMessage!.taskBookmarks;
    } else if (e.medium === Medium.SMS) {
      return e.textMessage!.taskBookmarks;
    }
    return [];
  };
  return (
    <>
      <ActivityFeedEntry
        highlighted={isCenteredEntry ? "#C8DBD7" : undefined}
        alignLeft={
          isBotResp ? false : loginLinkText || !(sender && sender.isAdvisor)
        }
        avatarIcon={
          isBotResp
            ? undefined
            : includeAvatar && (
                <Avatar sx={{ height: 40, width: 40 }}>
                  {sender && sender.firstName[0]}
                  {sender && sender.lastName ? sender.lastName[0] : ""}
                </Avatar>
              )
        }
        action={action}
        avatarGutter={!(loginLinkText || noAvatars)}
        headerLeftText={
          includeHeader ? (
            isBotMsg ? (
              <span
                style={{
                  fontSize: "12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {isBotResp && (
                  <Bot
                    size={12}
                    color={botColorScheme?.iconColor || "#8B5CF6"}
                  />
                )}
                {buildHeader()}
              </span>
            ) : (
              buildHeader()
            )
          ) : null
        }
        headerRightText={
          includeHeader ? (
            isBotMsg ? (
              <span style={{ fontSize: "12px" }}>{time}</span>
            ) : (
              time
            )
          ) : null
        }
        onBookmark={
          entry.medium !== Medium.TASK_COMMENT &&
          entry.medium !== Medium.TASK_CHANGE_EVENT &&
          entry.medium !== Medium.DELEGATION_TASK_COMMENT &&
          entry.medium !== Medium.DELEGATION_TASK_CREATED &&
          entry.medium !== Medium.DOCUMENT &&
          entry.medium !== Medium.SECRET
            ? onBookmark
            : undefined
        }
        taskBookmarks={entryToBookmarks(entry)}
      >
        {getSubcomponent(entry.medium)}
      </ActivityFeedEntry>
      {includeTodayDivider && <ActivityDivider text={"Today"} />}
    </>
  );
};
